import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Chip, CircularProgress, Typography } from '@mui/material';
import ROUTES from '../../common/Routing/Routes';
import {
  getShopItems,
  Merchandise,
  MerchandiseCategory,
  merchandiseCategoryLabels,
} from '../../common/Rest/Services/ShopService';
import { formatShopPrice, getShopImageSrcSet, getShopImageUrl } from './shopUtils';
import './ShopPage.css';

function ShopPage() {
  const [items, setItems] = useState<Merchandise[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState<MerchandiseCategory | null>(null);

  useEffect(() => {
    getShopItems('', 0, 100)
      .then((page) => setItems(page.items))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(
    () => Object.values(MerchandiseCategory).filter((value) => items.some((item) => item.category === value)),
    [items]
  );
  const visibleItems = category ? items.filter((item) => item.category === category) : items;

  return (
    <div className="shop-page default-container">
      <header className="shop-heading">
        <Typography variant="h3" component="h1">Крамничка</Typography>
        <Typography color="text.secondary">
          Книжкові шопери, чохли та інші дрібнички для читання. Їх можна купити разом з орендою книжок — так ви
          допомагаєте нам покривати витрати на доставку й сервери.
        </Typography>
      </header>

      {categories.length > 1 && (
        <div className="shop-filters">
          <Chip label="Усе" color={category === null ? 'primary' : 'default'} onClick={() => setCategory(null)} />
          {categories.map((value) => (
            <Chip
              key={value}
              label={merchandiseCategoryLabels[value]}
              color={category === value ? 'primary' : 'default'}
              onClick={() => setCategory(value)}
            />
          ))}
        </div>
      )}

      {loading && <CircularProgress className="shop-loading" />}

      {!loading && visibleItems.length === 0 && (
        <p className="shop-empty">Скоро тут з'являться товари. Зазирніть трохи згодом.</p>
      )}

      <div className="shop-grid">
        {visibleItems.map((item) => (
          <Link key={item.id} to={`${ROUTES.SHOP}/${item.id}`} className="shop-card">
            <div className="shop-card-image">
              <img
                src={getShopImageUrl(item, 'thumb')}
                srcSet={getShopImageSrcSet(item)}
                sizes="(max-width: 860px) 50vw, 240px"
                alt={item.name}
                loading="lazy"
                decoding="async"
              />
              {item.images.length > 1 && <span className="shop-card-photos">{item.images.length} фото</span>}
            </div>
            <div className="shop-card-copy">
              <h3>{item.name}</h3>
              <div className="shop-card-meta">
                <strong>{formatShopPrice(item.price)}</strong>
                <span className={item.stockCount > 0 ? '' : 'shop-out-of-stock'}>
                  {item.stockCount > 0 ? 'В наявності' : 'Немає в наявності'}
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default ShopPage;
